import { useCallback, useEffect, useReducer, useRef } from 'react';
import { createEventBus } from './js';

export type CookieOptions = {
  maxAge?: number;
  path?: string;
  sameSite?: 'Strict' | 'Lax' | 'None';
  secure?: boolean;
};

const buses = new Map<string, ReturnType<typeof createEventBus>>();

const getBus = (name: string) => {
  if (!buses.has(name)) buses.set(name, createEventBus());
  return buses.get(name)!;
};

export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  const rawValue = match.slice(name.length + 1);
  try {
    return decodeURIComponent(rawValue);
  } catch {
    return rawValue;
  }
};

const serializeCookie = (
  name: string,
  value: string | null,
  options: CookieOptions,
): string => {
  const parts = [`${name}=${value !== null ? encodeURIComponent(value) : ''}`];
  if (value === null) {
    parts.push('Max-Age=0');
  } else if (options.maxAge !== undefined) {
    parts.push(`Max-Age=${options.maxAge}`);
  }
  if (options.path) parts.push(`Path=${options.path}`);
  if (options.sameSite) parts.push(`SameSite=${options.sameSite}`);
  if (options.secure) parts.push('Secure');
  return parts.join('; ');
};

export const useCookie = (name: string, defaultOptions: CookieOptions = {}) => {
  const [, rerender] = useReducer((s) => s + 1, 0);
  const optionsRef = useRef(defaultOptions);
  optionsRef.current = defaultOptions;

  useEffect(() => getBus(name).subscribe(rerender), [name]);

  const setCookie = useCallback(
    (value: string | null, options?: CookieOptions) => {
      // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is async and not broadly supported
      document.cookie = serializeCookie(name, value, {
        ...optionsRef.current,
        ...options,
      });
      getBus(name).dispatch();
    },
    [name],
  );

  return [getCookie(name), setCookie] as const;
};
